/**
 * Personal: names, date of birth, gender (Male / Female), marital status and the
 * "hide my birthday" privacy switch. Saves only the fields that changed; a cleared field is sent
 * as null so the API clears it.
 */
import { useEffect, useMemo, useState } from 'react';
import { IconDeviceFloppy, IconRestore, IconAlertCircle } from '@tabler/icons-react';
import { Card, Stack, Group, Button, TextInput, Select, Switch, SimpleGrid, Text, Alert, DatePickerInput } from '@/components/ui';
import type { UpdateMemberRequest } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { useMaritalStatuses } from '../useMemberLookups';
import { useMemberPatch } from '../useMemberMutations';
import { GENDER_OPTIONS, dobDateOnly, dobToDate, dateToDob, textOrNull, errorMessage } from '../memberFormat';

interface PersonalForm {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: Date | null;
  gender: string | null;
  maritalStatusID: string | null;
  hideBirthdayFromColleagues: boolean;
}

/** Stored gender values are free text; map the common spellings onto Male / Female. */
function normaliseGender(value?: string | null): string | null {
  const v = (value ?? '').trim().toLowerCase();
  if (v === 'm' || v === 'male') return 'Male';
  if (v === 'f' || v === 'female') return 'Female';
  return null;
}

export function PersonalTab({ api, member, perms }: MemberTabProps) {
  const { options: maritalOptions } = useMaritalStatuses();
  const patch = useMemberPatch(api);

  const initial = useMemo<PersonalForm>(() => ({
    firstName: member.firstName ?? '',
    middleName: member.middleName ?? '',
    lastName: member.lastName ?? '',
    dateOfBirth: dobToDate(member.dateOfBirth),
    gender: normaliseGender(member.gender),
    maritalStatusID: member.maritalStatusID > 0 ? String(member.maritalStatusID) : null,
    hideBirthdayFromColleagues: !!member.hideBirthdayFromColleagues,
  }), [member]);

  const [form, setForm] = useState<PersonalForm>(initial);
  useEffect(() => setForm(initial), [initial]);

  const storedGender = (member.gender ?? '').trim();
  const unusualGender = !!storedGender && !normaliseGender(storedGender);

  const changes = useMemo<UpdateMemberRequest>(() => {
    const c: UpdateMemberRequest = {};
    if (form.firstName.trim() !== (member.firstName ?? '').trim()) c.firstName = form.firstName.trim();
    if ((textOrNull(form.middleName) ?? '') !== (member.middleName ?? '').trim()) c.middleName = textOrNull(form.middleName);
    if ((textOrNull(form.lastName) ?? '') !== (member.lastName ?? '').trim()) c.lastName = textOrNull(form.lastName);
    const dob = dateToDob(form.dateOfBirth);
    if ((dob ?? '') !== (dobDateOnly(member.dateOfBirth) ?? '')) c.dateOfBirth = dob;
    if (form.gender !== initial.gender) c.gender = form.gender;
    const marital = form.maritalStatusID ? Number(form.maritalStatusID) : null;
    if ((marital ?? 0) !== (member.maritalStatusID > 0 ? member.maritalStatusID : 0)) c.maritalStatusID = marital;
    if (form.hideBirthdayFromColleagues !== !!member.hideBirthdayFromColleagues) c.hideBirthdayFromColleagues = form.hideBirthdayFromColleagues;
    return c;
  }, [form, member, initial.gender]);

  const dirty = Object.keys(changes).length > 0;
  const firstNameError = !form.firstName.trim() ? 'First name is required' : null;
  const readOnly = !perms.edit;

  const save = () => {
    if (firstNameError || !dirty) return;
    patch.mutate(changes);
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <div>
          <Text fw={600}>Personal details</Text>
          <Text size="sm" c="dimmed">
            {api.mode === 'self' ? 'How you appear across the church systems.' : 'Names, birthday and family status.'}
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <TextInput label="First name" required value={form.firstName} error={firstNameError} disabled={readOnly}
            onChange={(e) => setForm({ ...form, firstName: e.currentTarget.value })} />
          <TextInput label="Middle name" value={form.middleName} disabled={readOnly}
            onChange={(e) => setForm({ ...form, middleName: e.currentTarget.value })} />
          <TextInput label="Surname" value={form.lastName} disabled={readOnly}
            onChange={(e) => setForm({ ...form, lastName: e.currentTarget.value })} />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <DatePickerInput label="Date of birth" placeholder="Pick a date" clearable disabled={readOnly}
            value={form.dateOfBirth} maxDate={new Date()} valueFormat="D MMMM YYYY"
            onChange={(v) => setForm({ ...form, dateOfBirth: (v as Date | null) ?? null })} />
          <Select label="Gender" placeholder="Choose" data={GENDER_OPTIONS} clearable disabled={readOnly}
            value={form.gender} onChange={(v) => setForm({ ...form, gender: v })}
            description={unusualGender ? `Currently recorded as "${storedGender}". Choose Male or Female to update it.` : undefined} />
          <Select label="Marital status" placeholder="Choose" data={maritalOptions} clearable disabled={readOnly}
            value={form.maritalStatusID} onChange={(v) => setForm({ ...form, maritalStatusID: v })} />
        </SimpleGrid>

        <Switch
          label="Hide my birthday from colleagues"
          description={api.mode === 'self'
            ? 'When on, your birthday does not appear on the staff calendar or the birthday widgets.'
            : 'When on, this birthday does not appear on the staff calendar or the birthday widgets.'}
          checked={form.hideBirthdayFromColleagues}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, hideBirthdayFromColleagues: e.currentTarget.checked })}
        />

        {patch.isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Not saved">
            {errorMessage(patch.error)}
          </Alert>
        )}

        {!readOnly && (
          <Group justify="flex-end">
            <Button variant="default" leftSection={<IconRestore size={16} />} disabled={!dirty || patch.isPending}
              onClick={() => { setForm(initial); patch.reset(); }}>
              Discard changes
            </Button>
            <Button leftSection={<IconDeviceFloppy size={16} />} disabled={!dirty || !!firstNameError} loading={patch.isPending} onClick={save}>
              Save personal details
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
